/**
 * Hooks para Créditos de Cliente (NC sobre factura pagada).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

export type CreditStatus = 'pending' | 'refunded' | 'applied'

export interface CustomerCredit {
  id: string
  customer: string
  customer_name: string
  /** De dónde salió el saldo: decide contra qué cuenta se salda. */
  origin: 'credit_note' | 'overpayment'
  origin_display: string
  /** El cobro cuyo excedente lo originó. Vacío si vino de una NC. */
  source_payment: string | null
  /** La NC que lo originó. Vacío si vino de un pago en exceso. */
  source_document: string | null
  source_folio: string | null
  source_reference_folio: string | null
  amount: string
  status: CreditStatus
  status_display: string
  resolved_at: string | null
  resolution_notes: string
  refund_date: string | null
  refund_method: string
  refund_reference: string
  refund_bank_account: string | null
  applied_to: string | null
  applied_to_folio: string | null
  applied_payment: string | null
  created_at: string
}

export function useCustomerCredits(params?: { status?: CreditStatus; customer_id?: string }) {
  return useQuery({
    queryKey: ['customer-credits', params],
    queryFn: async () => {
      const res = await apiClient.get<{ count: number; results: CustomerCredit[] }>(
        '/sales/credits/', { params },
      )
      return res.data.results
    },
  })
}

export interface RefundPayload {
  refund_date: string
  refund_method: 'transfer' | 'check' | 'cash' | 'card' | 'other'
  refund_reference: string
  bank_account_id: string
  notes?: string
}

export function useRefundCredit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ creditId, payload }: { creditId: string; payload: RefundPayload }) =>
      apiClient.post(`/sales/credits/${creditId}/refund/`, payload).then(r => r.data as CustomerCredit),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-credits'] })
      qc.invalidateQueries({ queryKey: ['vouchers'] })
    },
  })
}

export function useApplyCredit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ creditId, payload }: { creditId: string; payload: { target_document_id: string; notes?: string } }) =>
      apiClient.post(`/sales/credits/${creditId}/apply/`, payload).then(r => r.data as CustomerCredit),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-credits'] })
      qc.invalidateQueries({ queryKey: ['sale-documents'] })
    },
  })
}
